/* global nativeFunctions */
/**
 * MobiLoud Canvas Push Notification Registration
 *
 * This module handles push notification registration for MobiLoud Canvas wrapper apps.
 * It uses MobiLoud's nativeFunctions API to set the OneSignal external user ID,
 * enabling push notifications to be sent via the OneSignal API.
 *
 * Usage:
 *   import { initMobiLoudPush } from './mobiloud/push_registration';
 *   initMobiLoudPush();
 *
 * MobiLoud Native Functions (via nativeFunctions object):
 *   - nativeFunctions.onesignalSetExternalUserId(userId) - Sets external user ID in OneSignal
 *   - nativeFunctions.onesignalRemoveExternalUserId() - Removes external user ID
 *   - nativeFunctions.onesignalSendTags(tags) - Sends tags to OneSignal
 *
 * Detection:
 *   - Canvas app is detected via user agent containing 'canvas'
 *   - nativeFunctions available after 'nativeFunctionsLoaded' message event
 *
 * @see https://www.mobiloud.com/docs/what-are-native-functions
 */

const BADGE_SYNC_ENDPOINT = "/api/v1/me/badge/sync";

// Track initialization state to prevent double-init
let initialized = false;
let nativeFunctionsReady = false;

/**
 * Check if user is currently authenticated
 * Uses the window._tkcCurrentUserId set by Rails in application.html.erb
 * @returns {boolean}
 */
export function isUserAuthenticated() {
  return typeof window !== "undefined" && !!window._tkcCurrentUserId;
}

/**
 * Check if running inside MobiLoud Canvas wrapper
 * Detection is done via user agent containing 'canvas'
 * @returns {boolean}
 */
export function isMobiLoudEnvironment() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.indexOf("canvas") > -1;
}

/**
 * Get CSRF token from meta tag for API requests
 * @returns {string|null}
 */
function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute("content") : null;
}

/**
 * Set the OneSignal external user ID to link push notifications to this user
 * This is the recommended approach per MobiLoud documentation
 * @returns {boolean} Whether the external user ID was set
 */
function setOneSignalExternalUserId() {
  if (!isUserAuthenticated()) {
    console.log(
      "[MobiLoud Push] User not authenticated, skipping external user ID setup",
    );
    return false;
  }

  if (!nativeFunctionsReady) {
    console.warn("[MobiLoud Push] nativeFunctions not ready yet");
    return false;
  }

  const userId = String(window._tkcCurrentUserId);

  try {
    if (
      typeof nativeFunctions !== "undefined" &&
      nativeFunctions.onesignalSetExternalUserId
    ) {
      nativeFunctions.onesignalSetExternalUserId(userId);
      console.log(
        "[MobiLoud Push] SUCCESS: Set OneSignal external user ID:",
        userId,
      );

      // Log to server for debugging (fire-and-forget)
      logMobiLoudEvent("external_user_id_set", { userId });

      return true;
    } else {
      console.warn(
        "[MobiLoud Push] nativeFunctions.onesignalSetExternalUserId not available",
      );
      logMobiLoudEvent("native_function_unavailable", {
        nativeFunctionsDefined: typeof nativeFunctions !== "undefined",
        hasSetExternalUserId:
          typeof nativeFunctions !== "undefined" &&
          !!nativeFunctions.onesignalSetExternalUserId,
      });
      return false;
    }
  } catch (error) {
    console.error("[MobiLoud Push] Error setting external user ID:", error);
    logMobiLoudEvent("external_user_id_error", { error: error.message });
    return false;
  }
}

/**
 * Log MobiLoud events to server for debugging
 * Fire-and-forget, no error handling needed
 */
function logMobiLoudEvent(event, data) {
  const csrfToken = getCsrfToken();
  if (!csrfToken) return;

  // POST to a debug endpoint (create this endpoint for debugging)
  fetch("/api/v1/me/device_tokens/log_mobiloud_event", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
    },
    credentials: "same-origin",
    body: JSON.stringify({
      event,
      data,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    }),
  }).catch(() => {}); // Silently ignore errors
}

/**
 * Remove the OneSignal external user ID (call on logout)
 */
function removeOneSignalExternalUserId() {
  if (!nativeFunctionsReady) {
    return;
  }

  try {
    if (
      typeof nativeFunctions !== "undefined" &&
      nativeFunctions.onesignalRemoveExternalUserId
    ) {
      nativeFunctions.onesignalRemoveExternalUserId();
      console.log("[MobiLoud Push] Removed OneSignal external user ID");
    }
  } catch (error) {
    console.error("[MobiLoud Push] Error removing external user ID:", error);
  }
}

/**
 * Sync badge count when app opens
 * This refreshes the badge to reflect current unread items
 */
async function syncBadge() {
  // Only sync badge if user is authenticated
  if (!isUserAuthenticated()) {
    return;
  }

  const csrfToken = getCsrfToken();
  if (!csrfToken) return;

  try {
    const response = await fetch(BADGE_SYNC_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
    });
    if (response.ok) {
      const data = await response.json();
      console.log("[MobiLoud Push] Badge synced:", data.badge_count);
    }
  } catch (error) {
    console.error("[MobiLoud Push] Error syncing badge:", error);
  }
}

/**
 * Handle the nativeFunctionsLoaded event from MobiLoud
 * @param {MessageEvent} event
 */
function handleNativeFunctionsLoaded(event) {
  if (event.data && event.data === "nativeFunctionsLoaded") {
    console.log("[MobiLoud Push] nativeFunctions loaded");
    nativeFunctionsReady = true;

    // Set external user ID if user is authenticated
    if (isUserAuthenticated()) {
      setOneSignalExternalUserId();
    }

    // Sync badge on load
    syncBadge();
  }
}

/**
 * Initialize MobiLoud push notification handling
 * Call this on app initialization (safe to call multiple times)
 */
export function initMobiLoudPush() {
  // Debug info for troubleshooting
  if (typeof window !== "undefined") {
    const debugInfo = {
      isMobiLoudCanvas: isMobiLoudEnvironment(),
      userAgent: navigator.userAgent.substring(0, 50) + "...",
      userAuthenticated: isUserAuthenticated(),
      userId: window._tkcCurrentUserId || "none",
    };

    console.log("[MobiLoud Push] Init check:", JSON.stringify(debugInfo));

    // Show debug alert if URL has ?debug_mobiloud=1
    if (window.location.search.includes("debug_mobiloud=1")) {
      setTimeout(() => {
        alert("[MobiLoud Debug]\n" + JSON.stringify(debugInfo, null, 2));
      }, 2000);
    }
  }

  if (!isMobiLoudEnvironment()) {
    console.log(
      "[MobiLoud Push] Not in MobiLoud Canvas environment, skipping init",
    );
    return;
  }

  // Prevent double initialization
  if (initialized) {
    console.log("[MobiLoud Push] Already initialized, skipping");
    return;
  }
  initialized = true;

  console.log(
    "[MobiLoud Push] Initializing in MobiLoud Canvas environment. User authenticated:",
    isUserAuthenticated(),
    "User ID:",
    window._tkcCurrentUserId || "none",
  );

  // Listen for nativeFunctionsLoaded event
  window.addEventListener("message", handleNativeFunctionsLoaded, false);

  // Check if nativeFunctions is already available (in case event already fired)
  if (typeof nativeFunctions !== "undefined") {
    console.log("[MobiLoud Push] nativeFunctions already available");
    nativeFunctionsReady = true;

    if (isUserAuthenticated()) {
      setOneSignalExternalUserId();
    }
    syncBadge();
  }
}

/**
 * Manually trigger push notification setup (e.g., after login)
 * Call this after user authentication completes to ensure external user ID is set.
 * @returns {Promise<boolean>} Whether setup was successful
 */
export function registerMobiLoudDevice() {
  if (!isMobiLoudEnvironment()) {
    return Promise.resolve(false);
  }

  console.log("[MobiLoud Push] Manual registration triggered");

  return new Promise((resolve) => {
    if (nativeFunctionsReady) {
      const success = setOneSignalExternalUserId();
      resolve(success);
    } else {
      // Wait for nativeFunctions to be ready (with timeout)
      let attempts = 0;
      const checkInterval = setInterval(() => {
        attempts++;
        if (nativeFunctionsReady) {
          clearInterval(checkInterval);
          const success = setOneSignalExternalUserId();
          resolve(success);
        } else if (attempts >= 50) {
          // 5 second timeout (50 * 100ms)
          clearInterval(checkInterval);
          console.warn("[MobiLoud Push] Timeout waiting for nativeFunctions");
          resolve(false);
        }
      }, 100);
    }
  });
}

/**
 * Clear push notification setup (call on logout)
 */
export function clearMobiLoudDevice() {
  if (!isMobiLoudEnvironment()) {
    return;
  }
  removeOneSignalExternalUserId();
}

export default {
  isMobiLoudEnvironment,
  isUserAuthenticated,
  initMobiLoudPush,
  registerMobiLoudDevice,
  clearMobiLoudDevice,
};
